import useUpdateProposalPaymentSchedule from '@/api/mutations/proposals/useUpdateProposalPaymentSchedule';
import { PaymentScheduleInput } from '@/gql/graphql';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import PaymentScheduleEditor from '@/ui/pages/proposalBuilderPage/components/paymentScheduleEditor/PaymentScheduleEditor';
import {
  PAYMENT_SCHEDULE_FORM_SCHEMA,
  PaymentScheduleFormValues,
} from '@/ui/components/paymentScheduleStageCards/types';
import { toPaymentScheduleInput } from '@/utils/paymentScheduleInput';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type EditableStoredPaymentSchedule,
  fromEditableStoredPaymentSchedule,
} from '@payaca/helpers/editableStoredPaymentSchedule';
import { FC } from 'react';
import { useForm } from 'react-hook-form';

export type TProps = {
  /** Public ID of the Proposal whose payment schedule is being edited. */
  proposalId: string;
  /**
   * Proposal total in minor units, inc tax, used to display the monetary
   * amount alongside each stage's percentage.
   */
  proposalTotal: number;
  /** Schedule used to seed the form. Pass an empty schedule when none is set. */
  paymentSchedule: EditableStoredPaymentSchedule;
  className?: string;
};

/**
 * An empty schedule is sent as `null`, which clears it. So is an incomplete
 * one, which the schema resolver stops from ever being saved.
 */
const toPaymentScheduleMutationInput = (
  schedule: EditableStoredPaymentSchedule
): PaymentScheduleInput | null => {
  const paymentSchedule = fromEditableStoredPaymentSchedule(schedule);
  return paymentSchedule?.stages.length
    ? toPaymentScheduleInput(paymentSchedule)
    : null;
};

/**
 * Editable payment schedule form that auto-saves changes against the Proposal.
 * Invalid schedules are never saved: the schema resolver gates the auto-save,
 * so the server keeps the last valid state until the user resolves the errors.
 * Renders the form contents only — callers supply any surrounding chrome
 * (modal, tab, etc.).
 */
const ProposalPaymentScheduleForm: FC<TProps> = (props) => {
  const { proposalId, proposalTotal, paymentSchedule, className } = props;

  const { mutate: updatePaymentSchedule } = useUpdateProposalPaymentSchedule({
    proposalPublicId: proposalId,
  });

  const methods = useForm<PaymentScheduleFormValues>({
    defaultValues: { paymentSchedule },
    resolver: zodResolver(PAYMENT_SCHEDULE_FORM_SCHEMA),
    mode: 'onChange',
    // Auto-save attempts validate the whole form; don't yank focus to the
    // first invalid field while the user is typing elsewhere.
    shouldFocusError: false,
  });

  return (
    <AutoSaveForm
      className={className}
      methods={methods}
      // Compare the persisted payload so mid-edit churn that doesn't change
      // what gets saved (e.g. "50" vs 50) doesn't trigger a save.
      getComparable={({ paymentSchedule: schedule }) =>
        toPaymentScheduleMutationInput(schedule)
      }
      onSubmit={({ paymentSchedule: schedule }) => {
        updatePaymentSchedule({
          proposalId,
          paymentSchedule: toPaymentScheduleMutationInput(schedule),
        });
      }}
    >
      <PaymentScheduleEditor proposalTotal={proposalTotal} />
    </AutoSaveForm>
  );
};

export default ProposalPaymentScheduleForm;
